import { useEffect, useState } from 'react';
import PanelCard from 'components/card/PanelCard';
import useModal from 'components/modal/useModal';
import { paymentTermLabel } from 'constants/paymentTerms';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { Field, PlainField, TWO_COLUMN_GRID } from './overviewLayout';

export const BILLING_INFORMATION_UPDATED =
  'company_billing_information_updated';

/** "12 rue Lamartine, 97200 Fort-de-France", or an empty string. */
export const formatAddressLine = (address) =>
  address
    ? [
        address.street,
        [address.postcode, address.city].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

/**
 * Billing address and payment term of the company (JBWEB-1268, mockup 8.1).
 * Reads its own data: the company endpoint rebuilds the company field by field
 * and carries neither, and the card is the only reader.
 */
const CompanyBillingInformationCard = ({ idCompany, canEdit }) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const [billing, setBilling] = useState(null);
  const [status, setStatus] = useState('loading');
  const text = (key, fallback) =>
    language.getValue(`companyBilling.${key}`, fallback);

  useEffect(() => {
    if (!idCompany) return undefined;
    // A late answer for a company the panel has since left is dropped.
    let isCurrent = true;
    setStatus('loading');
    ApiDatabase.getCompanyBillingInformation(
      { idCompany },
      (response) => {
        if (!isCurrent) return;
        setBilling(response?.data ?? null);
        setStatus('ready');
      },
      (error) => {
        if (!isCurrent || error?.name === 'CanceledError') return;
        setStatus('error');
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [idCompany]);

  useEffect(() => {
    if (modalDataReturn?.type !== BILLING_INFORMATION_UPDATED) return;
    setBilling(modalDataReturn.billingInformation ?? null);
    setStatus('ready');
    setModalDataReturn({});
  }, [modalDataReturn]);

  const openEditor = () => {
    setModalData({ idCompany, billingInformation: billing });
    toggle('company_billing_information');
  };

  const notSet = text('notSet', 'Non renseigné');
  // A dash while it loads: "Non renseigné" would read as a settled answer.
  const shown = (value) => (status === 'loading' ? '—' : value || notSet);

  return (
    <PanelCard
      title={text('cardTitle', 'Informations de facturation')}
      collapsible
      onEdit={canEdit && status === 'ready' ? openEditor : null}
      contentClassName={status === 'error' ? 'px-4 py-3' : TWO_COLUMN_GRID}
    >
      {status === 'error' ? (
        <div className={'text-red-500'}>
          {text(
            'loadError',
            'Les informations de facturation n’ont pas pu être chargées.',
          )}
        </div>
      ) : (
        <>
          <Field
            label={text('billingAddress', 'Adresse de facturation')}
            value={shown(formatAddressLine(billing?.billingAddress))}
          />
          <PlainField
            label={text('paymentTerm', 'Délai de paiement')}
            value={shown(paymentTermLabel(billing?.paymentTerm, language))}
          />
        </>
      )}
    </PanelCard>
  );
};

export default CompanyBillingInformationCard;
